// Match Results widget — fixtures/results for the filter bar's league + season
// (consumes [league, season]), with a client-side team + played filter. Data
// from GET /matches; the narrowing is pure (matchFilter.ts, unit-tested).

import { useMemo, useState } from "react";
import { useMatches } from "../data/leagues.js";
import type { WidgetScope } from "./scope.js";
import { filterMatches, teamsFromMatches } from "./matchFilter.js";
import { theme } from "../lib/theme.js";

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}

export default function MatchResultsWidget({ scope }: { scope: WidgetScope }) {
  const { data, isPending, isError, error } = useMatches(scope.competitionId, scope.seasonId);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [onlyPlayed, setOnlyPlayed] = useState(false);

  const all = data?.matches ?? [];
  const teams = useMemo(() => teamsFromMatches(all), [all]);
  const rows = useMemo(() => filterMatches(all, { teamId, onlyPlayed }), [all, teamId, onlyPlayed]);

  if (scope.competitionId === null || scope.seasonId === null)
    return <Msg>Pick a league and season above.</Msg>;
  if (isPending) return <Msg>Loading matches…</Msg>;
  if (isError) return <Msg danger>Couldn't load matches. {String(error)}</Msg>;
  if (all.length === 0) return <Msg>No matches for this season yet.</Msg>;

  return (
    <div>
      {/* Client-side filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select
          aria-label="Filter by team"
          value={teamId ?? ""}
          onChange={(e) => setTeamId(e.target.value === "" ? null : Number(e.target.value))}
          style={{
            background: theme.input,
            color: theme.ink,
            border: `1px solid ${theme.borderInput}`,
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 13,
          }}
        >
          <option value="">All teams</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: theme.inkMuted, fontSize: 13 }}>
          <input type="checkbox" checked={onlyPlayed} onChange={(e) => setOnlyPlayed(e.target.checked)} />
          Played only
        </label>
        <span style={{ color: theme.inkFaint, fontSize: 12, marginLeft: "auto" }}>{rows.length} matches</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {rows.map((mt) => {
          const played = mt.homeScore !== null && mt.awayScore !== null;
          return (
            <div
              key={mt.matchId}
              style={{
                display: "grid",
                gridTemplateColumns: "88px 1fr auto 1fr",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                borderRadius: 6,
                background: theme.field,
                fontSize: 13,
              }}
            >
              <span style={{ color: theme.inkFaint, fontVariantNumeric: "tabular-nums" }}>{mt.matchDate}</span>
              <span style={{ color: theme.ink, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {mt.homeTeamName}
              </span>
              <span
                style={{
                  color: played ? theme.ink : theme.inkFaint,
                  fontVariantNumeric: "tabular-nums",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  background: played ? theme.accentSurface : "transparent",
                  borderRadius: 5,
                  padding: "1px 8px",
                  minWidth: 46,
                  textAlign: "center",
                }}
              >
                {played ? `${mt.homeScore}–${mt.awayScore}` : "v"}
              </span>
              <span style={{ color: theme.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {mt.awayTeamName}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
